'use client';

// Shared "re-read when the operator returns to this browser tab" effect.
//
// Extracted in BUG-1099 (sharing.md § 4, reuse-first): the auction-detail
// chrome needed the same refresh-on-tab-focus behaviour `useAuctionBidders`
// already had inline, and a second hand-rolled copy would have been the third
// place this listener's contract lived.
//
// `visibilitychange` only — deliberately NOT paired with a `focus` listener.
// The two fire back-to-back on tab return and would double-fetch.

import { useEffect, useRef } from 'react';

/** Options for {@link useVisibilityRefresh}. */
export interface VisibilityRefreshOptions {
    /**
     * Install the listener. Pass `false` while the id the refresh depends on is
     * still unresolved. Defaults to `true`.
     */
    enabled?: boolean;
    /**
     * Minimum gap between two visibility-triggered refreshes, in ms. `0` (the
     * default) refreshes on every tab return. Use a floor when the refresh hits
     * an expensive endpoint and a rapid alt-tabber could otherwise hammer it.
     */
    floorMs?: number;
}

/**
 * Run `refresh` whenever the document becomes visible again.
 *
 * The floor is tracked per listener installation, and resets whenever `refresh`
 * changes identity — for a `useCallback` keyed on a route id that means
 * navigating to a different record always allows an immediate first refresh
 * rather than inheriting the previous record's cooldown.
 *
 * @param refresh - called on each qualifying tab return; may be async.
 * @param options - `enabled` and `floorMs`; see {@link VisibilityRefreshOptions}.
 */
export function useVisibilityRefresh(
    refresh: () => void | Promise<void>,
    { enabled = true, floorMs = 0 }: VisibilityRefreshOptions = {},
): void {
    const lastRefreshAtRef = useRef(0);

    useEffect(() => {
        if (!enabled) return;
        lastRefreshAtRef.current = 0;

        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            if (floorMs > 0) {
                const now = Date.now();
                if (now - lastRefreshAtRef.current < floorMs) return;
                lastRefreshAtRef.current = now;
            }
            void refresh();
        };

        document.addEventListener('visibilitychange', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [enabled, floorMs, refresh]);
}
